/**
 * Contractor auth — POST /api/auth/login + /api/auth/register (public, no JWT required).
 */

import { apiFetch, resolveApiUrl, setAccessToken } from './client'

export interface ContractorAuthUser {
  id: number
  name: string
  email: string
  email_verified?: boolean
  trial_start_date?: string
  trial_end_date?: string
  subscription_status?: string
  plan_type?: string
  user_role: string | null
  portal_session?: 'vendor' | null
  company_name?: string | null
  company_role?: 'owner' | 'admin' | 'finance' | null
  has_completed_onboarding?: boolean
  needs_portal_choice?: boolean
}

export interface ContractorLoginResult {
  token: string
  user: ContractorAuthUser
  redirect_to?: string
  needs_portal_choice?: boolean
  capabilities?: {
    has_contractor_projects: boolean
    has_vendor_portal: boolean
    vendor_portals: Array<{ company_name: string; contractor_name: string }>
  }
}

export type ContractorRegisterResult = ContractorLoginResult

/** Persist access token for PDF ?token= URLs; refresh cookie is httpOnly. */
export function storeContractorSession(token: string): void {
  setAccessToken(token)
}

export async function contractorLogin(
  email: string,
  password: string,
  keepSignedIn = true,
): Promise<ContractorLoginResult> {
  const res = await fetch(resolveApiUrl('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, keepSignedIn }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { message?: string; error?: string }).message
      ?? (err as { error?: string }).error
      ?? 'Invalid email or password')
  }

  const data = (await res.json()) as ContractorLoginResult
  if (data.token) storeContractorSession(data.token)
  if (data.user) {
    data.user.needs_portal_choice = data.needs_portal_choice
    data.user.portal_session = data.user.portal_session ?? null
  }
  return data
}

/**
 * Switch contractor ↔ vendor portal session.
 * Must send Bearer — CSRF gate ignores ci_access cookie on POST
 * (see resolveAuthJwt). Bare fetch with credentials alone → 401
 * "Bearer token required".
 */
export async function selectPortal(portal: 'contractor' | 'vendor'): Promise<ContractorLoginResult> {
  const data = await apiFetch<ContractorLoginResult>('POST', '/api/auth/select-portal', { portal })
  if (data.token) storeContractorSession(data.token)
  return data
}

export async function contractorForgotPassword(email: string): Promise<void> {
  const res = await fetch(resolveApiUrl('/api/auth/forgot-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? 'Failed to send reset email')
  }
}

export async function contractorRegister(
  name: string,
  email: string,
  password: string,
  companyName: string,
  keepSignedIn = true,
  turnstileToken?: string | null,
): Promise<ContractorRegisterResult> {
  const res = await fetch(resolveApiUrl('/api/auth/register'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      name,
      email,
      password,
      company_name: companyName,
      keepSignedIn,
      ...(turnstileToken ? { turnstileToken } : {}),
    }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? 'Registration failed')
  }

  const data = (await res.json()) as ContractorRegisterResult
  if (data.token) storeContractorSession(data.token)
  return data
}

export type InvitePreview = {
  email: string
  role: string
  role_label: string
  company_name: string | null
  inviter_name: string | null
  inviter_email: string | null
  has_account: boolean
  viewer: 'anonymous' | 'match' | 'mismatch'
}

function inviteHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' }
  const t = localStorage.getItem('ci_token')
  if (t) headers.Authorization = `Bearer ${t}`
  return headers
}

export async function loadInvitePreview(token: string): Promise<InvitePreview> {
  const res = await fetch(`/api/team/invite/${encodeURIComponent(token)}`, {
    headers: inviteHeaders(),
    credentials: 'include',
  })
  const body = await res.json().catch(() => ({})) as { data?: InvitePreview; error?: string; message?: string }
  if (!res.ok) throw new Error(body.error || body.message || 'Invalid invite')
  return body.data as InvitePreview
}

export async function joinInvite(
  token: string,
  body: { name?: string; password?: string } = {},
): Promise<{ redirect: string; token?: string | null }> {
  const res = await fetch(`/api/auth/accept-invite/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: inviteHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const payload = await res.json().catch(() => ({})) as {
    data?: { redirect?: string; token?: string }
    error?: string
    message?: string
  }
  if (!res.ok) throw new Error(payload.error || payload.message || 'Accept failed')
  if (payload.data?.token) storeContractorSession(payload.data.token)
  return { redirect: payload.data?.redirect || '/dashboard?invite_accepted=1', token: payload.data?.token }
}
